import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Grid,
  TextField,
  FormLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Iconify from "../components/iconify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { get, post, put } from "src/utils/networkUtils";
import MarketplacePackSettings, { validateGradePull } from "src/components/packs-admin/MarketplacePackSettings";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { createBlobInContainer } from "src/utils/azureBlob";
import { nhlPackTypes } from "src/utils/cardUtils";
import useWithUrlTenant from "src/hooks/useTenant";

const dateFormat = "YYYY-MM-DD HH:mm:ss";
const defaultPack = {
  name: "",
  description: "",
  image: "",
  price: "",
  quantity: 100,
  cardQuantity: 3,
  packSettings: null,
  publishDate: moment().format(dateFormat),
  expiryDate: moment().format(dateFormat),
  order: 99,
  unlocksXpLevelId: 1,
};

export default function MarketplacePackPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pack, setPack] = useState(null);
  const [packId, setPackId] = useState(null);
  const [xpLevels, setXpLevels] = useState([]);
  const [packTypes, setPackTypes] = useState(null);
  const [packSettingsValid, setPackSettingsValid] = useState(false);
  const [packSettingsTotal, setPackSettingsTotal] = useState(0);
  const [previousTenantId, setPreviousTenantId] = useState(null);
  const [searchParams] = useSearchParams();
  const withTenant = useWithUrlTenant();
  const tenantId = searchParams.get("tenantId");

  // Fetch xp-level-settings (non-tenant specific, fetch only once)
  useEffect(() => {
    get(`/admin/xp-level-settings`).then(function (response) {
      if (response.settings.length) {
        setXpLevels(response.settings);
      }
    });
  }, []);

  // Fetch pack types (non-tenant specific, fetch only once)
  useEffect(() => {
    get(`/admin/packs/types`).then(function (response) {
      setPackTypes(
        response.packTypes.filter((pack) => nhlPackTypes.includes(pack.value))
      );
    });
  }, []);

  // Fetch or initialize pack data (tenant specific)
  useEffect(() => {
    if (searchParams.has("pack_id")) {
      let packId = searchParams.get("pack_id");
      setPackId(packId);
      get(withTenant(`/admin/marketplace/scoreable-packs/${packId}`)).then(
        function (response) {
          setPack(response.pack);
        }
      );
    } else {
      setPack(defaultPack);
    }
  }, [searchParams, withTenant]);

  // Redirect to marketplace packs when tenant changes while editing
  useEffect(() => {
    if (packId && previousTenantId !== null && previousTenantId !== tenantId) {
      navigate(withTenant(`/dashboard/marketplace-packs`));
      return;
    }
    setPreviousTenantId(tenantId);
  }, [tenantId, packId, previousTenantId, navigate, withTenant]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setPack((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleChangePackSettings = useCallback((packSettings) => {
    setPack((prevState) => ({
      ...prevState,
      packSettings,
    }));
  }, []);

  const handlePackSettingsValidation = useCallback((isValid, totalCards) => {
    setPackSettingsValid(isValid);
    setPackSettingsTotal(totalCards);
  }, []);

  const handleDateChange = (newValue, fieldName) => {
    setPack((prevState) => ({
      ...prevState,
      [fieldName]: moment(newValue).format(dateFormat),
    }));
  };
  const handleTypeChange = (value) => {
    setPack((prevState) => ({
      ...prevState,
      packType: value,
    }));
  };

  const allowToSave = () => {
    if (!pack || !pack.name || pack.name.trim().length === 0) {
      return false;
    }
    if (!pack.image || pack.image.trim().length === 0) {
      return false;
    }
    if (!pack.price || isNaN(parseFloat(pack.price)) || parseFloat(pack.price) <= 0) {
      return false;
    }
    if (!pack.quantity || pack.quantity === 0) {
      return false;
    }
    if (!pack.packSettings) {
      return false;
    }
    if (!pack.cardQuantity || pack.cardQuantity === 0) {
      return false;
    }
    
    // Check pack settings validation from child component
    if (!packSettingsValid) {
      return false;
    }

    // Check card quantity equals pack settings total
    if (packSettingsTotal !== parseInt(pack.cardQuantity)) {
      return false;
    }

    // Validate gradePull separately if provided (gradePull is independent)
    if (pack.packSettings?.gradePull) {
      const gradePullValidation = validateGradePull(pack.packSettings.gradePull);
      if (!gradePullValidation.isValid) {
        return false;
      }
    }

    return true;
  };

  const savePack = () => {
    if (!allowToSave()) return;

    setLoading(true);
    
    if (packId) {
      put(`/admin/marketplace/scoreable-packs/${packId}`, pack)
        .then(function () {
          navigate(withTenant(`/dashboard/marketplace-packs`));
        })
        .catch(function (error) {
          console.error("Error updating pack:", error);
        })
        .finally(() => setLoading(false));
    } else {
      pack.tenantId = tenantId;
      post(`/admin/marketplace/scoreable-packs`, pack)
        .then(function () {
          navigate(withTenant(`/dashboard/marketplace-packs`));
        })
        .catch(function (error) {
          console.error("Error creating pack:", error);
        })
        .finally(() => setLoading(false));
    }
  };
  const uploadFiles = (event, name) => {
    if (event.target.files.length === 0) {
      return;
    }

    const files = event.target.files;

    const uploadFilesAsync = async () => {
      try {
        await createBlobInContainer(files[0]);

        const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
        const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
        setPack((prevState) => ({
          ...prevState,
          [name]: `https://${storageAccountName}.blob.core.windows.net/${containerName}/${files[0].name}`,
        }));
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    };

    uploadFilesAsync();
  };

  const handleUnlocksAtXpChange = (value) => {
    setPack((prevState) => ({
      ...prevState,
      unlocksXpLevelId: value,
    }));
  };

  return (
    <>
      <Helmet>
        <title> Marketplace Pack </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Marketplace Pack
          </Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:save-outline" />}
            onClick={() => savePack()}
            disabled={!allowToSave()}
          >
            Save
          </Button>
        </Stack>

        <Card sx={{ p: 2 }}>
          {pack === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container>
              <Grid item xs={4} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="name"
                  label="Name"
                  fullWidth
                  required
                  value={pack.name}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={8} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="description"
                  label="Description"
                  fullWidth
                  value={pack.description}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="order"
                  label="Order"
                  fullWidth
                  required
                  value={pack.order}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="price"
                  label="Price"
                  fullWidth
                  required
                  type="number"
                  value={pack.price}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="quantity"
                  label="Quantity"
                  fullWidth
                  required
                  value={pack.quantity}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={2} sx={{ m: 0, p: 1 }}>
                <TextField
                  id="cardQuantity"
                  label="Cards Quantity"
                  fullWidth
                  required
                  value={pack.cardQuantity}
                  variant="standard"
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}
                  required>
                  <DateTimePicker
                    label="Publish Date"
                    value={moment(pack.publishDate, dateFormat)}
                    required
                    onChange={(value) => handleDateChange(value, "publishDate")}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    label="Expiry Date"
                    value={moment(pack.expiryDate, dateFormat)}
                    onChange={(value) => handleDateChange(value, "expiryDate")}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={2} sx={{ m: 0, p: 1 }}>
                <FormControl sx={{ m: 1, minWidth: 160 }} size="small">
                  <InputLabel id="demo-simple-select-label" required>
                    Pack Type
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={pack.packType ?? 0}
                    label="Competition"
                    onChange={(event) => handleTypeChange(event.target.value)}
                  >
                    {packTypes !== null &&
                      packTypes.map((item) => (
                        <MenuItem
                          value={item.value}
                          key={`competition-${item.value}`}
                        >
                          {item.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3} sx={{ m: 0, p: 1 }}>
                <FormControl sx={{ m: 1, minWidth: 160 }} size="small">
                  <InputLabel id="xp-level-select-label" required>
                    Unlocks at XP
                  </InputLabel>
                  <Select
                    labelId="xp-level-select-label"
                    id="xp-level-select"
                    value={pack?.unlocksXpLevelId}
                    label="Unlocks at XP"
                    onChange={(event) =>
                      handleUnlocksAtXpChange(event.target.value)
                    }
                  >
                    {xpLevels !== null &&
                      xpLevels.map((level) => (
                        <MenuItem
                          value={level.levelId}
                          key={`xp-level-${level.levelId}`}
                        >
                          Level {level.levelName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={3}>
                <FormLabel id="card-format-label" required>
                  Pack Image
                </FormLabel>
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="label"
                >
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={(event) => uploadFiles(event, "image")}
                  />
                  <Iconify icon={"eva:upload-outline"} sx={{ mr: 2 }} />
                </IconButton>
                {pack.image && (
                  <img
                    style={{
                      margin: "0 auto",
                      width: 100,
                      background: "grey",
                    }}
                    alt="Pack"
                    src={pack.image}
                  />
                )}
              </Grid>
            </Grid>
          )}
        </Card>

        <Card sx={{ p: 2, mt: 2 }}>
          {pack !== null && (
            <MarketplacePackSettings
              packSettings={pack.packSettings}
              packSettingsChangeHandler={handleChangePackSettings}
              onValidationChange={handlePackSettingsValidation}
              cardQuantity={parseInt(pack.cardQuantity)}
            />
          )}
        </Card>
      </Container>
    </>
  );
}
